import React, { useState, useEffect } from "react";

// ESRI
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import JobInfo from "@arcgis/core/rest/support/JobInfo.js";

// lIbraries
import { Button, Icon, Dropdown, Popup } from "semantic-ui-react";
import axios from "axios";

// Custom
import DrawHandler from "./DrawHandler";

const PrintServiceWidget = ({
  url,
  view,
  activeTool,
  deactivateTool,
  activateTool,
  setTooltipMessage,
  setTooltipVisible,
  localConfig,
  setLocalConfig,
}) => {
  const [status, setStatus] = useState(false);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [layer, setLayer] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [drawHandler, setDrawHandler] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // console.log("PrintServiceWidget", { url, activeTool, status, job });

  useEffect(() => {
    const print_layer = new GraphicsLayer({
      id: "PrintServiceLayer",
      // print: true,
    });
    view.map.layers.add(print_layer);
    setLayer(print_layer);

    // Widgets for the drawing tool
    const draw_handler = new DrawHandler({
      view: view,
      layer: print_layer,
    });

    draw_handler.on("completed", (geometry) => {
      // Submit the job
      submitJob(geometry.extent);
      setTooltipMessage(null);
      setTooltipVisible(false);

      // Clear the tooltip
      // this.cancelTooltip();

      setTimeout(() => {
        // Clear the graphics
        print_layer.graphics.removeAll();

        // If the restart tool is set then activate it
        // if (this.restart_tool) {
        activateTool({ tool: "Identify" });
      }, 500);
    });

    setDrawHandler(draw_handler);

    // Check if there are any active print reqeusts
    if (localConfig.print_request) {
      checkPrintStatus(localConfig.print_request);
    }
  }, []);

  const checkPrintStatus = async (job_id) => {
    console.error({ job_id, url });

    let fetchReport = () => axios.get(`${url}/jobs/${job_id}?f=json`);
    let validate = (result) => {
      console.log("validate", { result });

      // Catch if the print fails
      if (result.data.jobStatus === "esriJobFailed") {
        setErrorMessage("Print Request failed. Click to clear.");
        setStatus("error");
        return false;
      }

      // Cancel if error
      if (result.data.error) {
        setErrorMessage("Print Request no longer exists. Click to clear.");
        setStatus("error");
        return false;
      }

      if (
        ["esriJobCancelled", "esriJobCancelling"].includes(
          result.data.jobStatus
        )
      ) {
        reset();
        return false;
      }

      // Cancel if the job succeeded
      if (result.data.jobStatus === "esriJobSucceeded") {
        // setStatus("finished");
        // setFileUrl(result.data.results.value.url);
        // setJob(null);
        return false;
      }

      if (result.data.jobStatus === "esriJobExecuting") {
        setStatus("active");
      }

      // continue looping
      return true;
    };
    let response = await poll(fetchReport, validate, 5000);

    if (response.data.jobStatus === "esriJobSucceeded") {
      axios
        .get(`${url}/jobs/${job_id}/results/output_file?f=json`)
        .then((result) => {
          console.log("GET FILE", { result });
          setStatus("finished");
          setFileUrl(result.data.value.url);
          setJob(null);
        });
    }

    console.error("Response", { response });
  };

  const poll = async function (fn, fnCondition, ms) {
    let result = await fn();
    console.log("RESULT", { result });
    while (fnCondition(result)) {
      await wait(ms);
      result = await fn();
    }
    return result;
  };

  const wait = function (ms = 1000) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  };

  const handleCurrentExtent = () => {
    submitJob(view.extent);
  };

  const handleCustomExtent = () => {
    if (activeTool.tool == "Identify") {
      deactivateTool();
    }
    drawHandler.start("rectangle");
    setTooltipMessage(
      "Click once to start drawing the rectangle, click again to complete the feature"
    );
    setTooltipVisible(true);
  };

  const reset = () => {
    setStatus(false);
    setFileUrl(null);
    var new_config = { ...localConfig };
    new_config.print_request = null;

    setLocalConfig(new_config);
  };

  const cancelJob = () => {
    // console.log("cancel job");
    if (job) {
      job.cancelJob(job.jobId);
    }
    reset();
  };

  const submitJob = async (extent) => {
    // console.error("submitJob");

    // Set the web_map_as_json default object
    var web_map_as_json = {
      operationalLayers: [],
      mapOptions: {
        showAttribution: false,
        extent: {
          xmin: extent.xmin,
          ymin: extent.ymin,
          xmax: extent.xmax,
          ymax: extent.ymax,
          spatialReference: {
            wkid: 102676,
          },
        },
      },
      exportOptions: {
        outputSize: [800, 1100],
        dpi: 96,
      },
    };

    // Return all layers that are set to print and visible
    var print_layers = view.map.layers.filter((record) => {
      return record.print && record.visible;
    });

    // Returns all the visible layers
    var layers = print_layers.filter((record) => {
      return record.type != "graphics";
    });

    // Build an array of sublayers for concating
    var sublayers = [];

    // Check if layers and if so then pluck the sublayers out
    if (layers.items.length > 0) {
      layers.forEach((record) => {
        // Filter and return any sub layers that are visible
        sublayers = sublayers.concat(
          record.sublayers.items
            .filter((item) => {
              return item.visible;
            })
            .map((item) => {
              return item.title;
            })
        );
      });
    }

    // Returns all the visible graphics layers that contains graphics
    var graphics = print_layers.filter((record) => {
      return record.type == "graphics" && record.graphics.length > 0;
    });

    if (view.graphics.length > 0) {
      graphics.items.concat(
        view.graphics.items.filter((record) => {
          return record;
        })
      );
    }

    // check if there is any graphics
    if (graphics.length > 0) {
      var graphic_obj = {
        text: {
          name: "graphic_layer",
          geometry: "esriGeometryPoint",
          features: [],
        },
        point: {
          name: "graphic_layer",
          geometry: "esriGeometryPoint",
          features: [],
        },
        polygon: {
          name: "graphic_layer",
          geometry: "esriGeometryPolygon",
          features: [],
        },
        polyline: {
          name: "graphic_layer",
          geometry: "esriGeometryPolyline",
          features: [],
        },
      };

      graphics.items.forEach((record) => {
        var obj = {
          id: record.id,
          opacity: 1,
          minScale: record.minScale,
          maxScale: record.maxScale,
          featureCollection: {
            layers: [],
          },
        };

        // Iterate the items and convert the geometry to json
        record.graphics.items.forEach((graphic) => {
          // Remove all attributes
          var g = graphic.toJSON();
          g.attributes = {};

          if (
            graphic.geometry.type === "point" &&
            graphic.symbol.type === "text"
          ) {
            graphic_obj["text"].features.push(g);
          } else {
            graphic_obj[graphic.geometry.type].features.push(g);
          }
        });

        // Iterate the graphic object and push them
        for (var key in graphic_obj) {
          if (graphic_obj[key].features.length > 0) {
            obj.featureCollection.layers.push({
              layerDefinition: {
                name: graphic_obj[key].name,
                geometryType: graphic_obj[key].geometry,
              },
              featureSet: {
                geometryType: graphic_obj[key].geometry,
                features: graphic_obj[key].features,
              },
            });
          }
        }

        // Add the layer to the webmap as json parent
        web_map_as_json.operationalLayers.push(obj);
      });
    }

    var parameters = {
      web_map_as_json: web_map_as_json,
      layers: sublayers,
    };

    // console.log("parameters", JSON.stringify(parameters));
    // console.log("---------------------");

    setStatus("active");
    // console.log({ url, parameters });

    // geoprocessor.execute(`${url}/submitJob`, parameters).then((result) => {
    //   console.log(result);
    // });
    const job = await geoprocessor.submitJob(url, parameters);
    setJob(job);

    console.error({ job });

    var new_config = { ...localConfig };
    new_config.print_request = job.jobId;

    setLocalConfig(new_config);

    // // Define a callback that will be called periodically. The function will print the
    // // geoprocessor's progress and percentage complete (if a step progressor).
    // const statusCallback = (response) => {
    //   console.log({ response });
    //   if (
    //     ["esriJobFailed", "esriJobCancelled", "esriJobCancelling"].includes(
    //       response.jobStatus
    //     )
    //   ) {
    //     alert("The Print Export failed to generate.");
    //     reset();
    //   }
    // };

    // Wait for the geoprocessing job to complete and print job progress
    // to the console every five seconds
    // await job
    //   .waitForJobCompletion({ interval: 5000, statusCallback })
    //   .then((result) => {
    //     // console.error({ result, job });
    //     job.fetchResultData("output_file").then((result) => {
    //       // console.log("job data", result);
    //       setStatus("finished");
    //       setFileUrl(result.value.url);
    //       setJob(null);
    //     });
    //   });

    checkPrintStatus(job.jobId);
  };

  const checkDropdownVisibility = () => {
    const $dropdown_menu = document.getElementById("print_dropdown_menu");
    if ($dropdown_menu.classList.contains("visible")) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  if (status === "error") {
    return (
      <Popup
        trigger={
          <Button
            icon
            negative
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => reset()}
          >
            <Icon name={"exclamation"} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content={
          errorMessage
            ? errorMessage
            : "An Error has occurred. Print Task Failed."
        }
        position="right center"
      />
    );
  }

  if (status === "active") {
    return (
      <Popup
        trigger={
          <Button
            icon
            secondary
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => cancelJob()}
          >
            <Icon name={"notched circle"} loading style={{ height: "1em" }} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content="Processing Print Request. Click to Cancel."
        position="right center"
      />
    );
  }

  if (status === "finished") {
    return (
      <Popup
        trigger={
          <Button
            as="a"
            target="_blank"
            href={fileUrl}
            icon
            positive
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => {
              reset();
            }}
          >
            <Icon name={"checkmark"} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content="Print Request Completed"
        position="right center"
      />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Popup
        content="Print"
        position="right center"
        open={open}
        className={`transition fade in`}
        inverted
        size="tiny"
        trigger={
          <Dropdown
            icon="print"
            pointing="left"
            className="ui icon button no-margin no-focus"
            onMouseEnter={() => checkDropdownVisibility()}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen(false)}
          >
            <Dropdown.Menu id="print_dropdown_menu">
              <Dropdown.Header content="Print Map" />
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleCurrentExtent}>
                <Icon className="expand arrows alternate" />
                Current Extent
              </Dropdown.Item>
              <Dropdown.Item onClick={handleCustomExtent}>
                <Icon className="expand" />
                Custom Extent
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />
    </div>
  );
};

export default PrintServiceWidget;
